import React, { useEffect, useState, useCallback } from 'react';
import useInterval from 'use-interval';
import { auth } from '../firebase/setup';
import PropTypes from 'prop-types';
import { getCookieValue, setCookie, clearCookie } from '../cookies';
import AppLoader from './AppLoader';
import { signInWithCustomToken, signOut } from 'firebase/auth';

const getTokenExpirationTimeout = decodedToken =>
  decodedToken.exp * 1000 - new Date().getTime();

const isExpired = decodedToken => {
  return getTokenExpirationTimeout(decodedToken) < 0;
};

const useFirebaseToken = (forceAuthentication = true) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);

  const loginToken = getCookieValue('loginToken');

  const refreshToken = useCallback(
    (forceRefresh = false) => user?.getIdToken(forceRefresh).then(setToken),
    [user],
  );

  useEffect(() => {
    let signingOut = false;
    const unsubscribe = auth.onIdTokenChanged(
      fbUser => {
        if (!fbUser) {
          if (
            !getCookieValue('loginToken') &&
            forceAuthentication &&
            !signingOut
          ) {
            signingOut = true;
            signOut(auth).then(() => {
              window.location.href = '/logout';
              return true;
            });
          }
        }
        setUser(fbUser);
      },
      e => console.warn(e),
      () => console.info('user logout'),
    );
    return () => unsubscribe();
  }, [forceAuthentication]);

  useEffect(() => {
    if (user) {
      refreshToken();
    }
  }, [user]);

  useEffect(() => {
    if (loginToken) {
      signInWithCustomToken(auth, loginToken)
        .then(() => clearCookie('loginToken'))
        .catch(err => {
          clearCookie('loginToken');
          window.location.href = '/logout';
          console.error(err);
        });
    }
  }, [loginToken]);

  return { token, refreshToken: () => refreshToken(true) };
};

const AuthManager = ({ color, forceAuthentication, children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState();
  const { token, refreshToken } = useFirebaseToken(forceAuthentication);
  const [decodedToken, setDecodedToken] = useState(null);

  useInterval(() => {
    if (decodedToken && isExpired(decodedToken)) {
      refreshToken();
    }
  }, 30 * 1000);

  useEffect(() => {
    if (!token) {
      setDecodedToken(null);
      return;
    }
    try {
      setDecodedToken(JSON.parse(atob(token.split('.')[1])));
    } catch {}
  }, [token]);

  useEffect(() => {
    if (!decodedToken || isExpired(decodedToken)) {
      setIsAuthenticated(!forceAuthentication);
      return;
    }

    setCookie('token', token);
    fetch(`/users/me`)
      .then(response => response.json())
      .then(data => {
        setCookie('user', JSON.stringify(data));
        window.user = data;
      })
      .then(() => {
        setIsAuthenticated(true);
      })
      .catch(e => {
        console.error('Error fetching user', e);
        // setIsAuthenticated(false); // there is something really wrong here. If error, we're stuck in loading state... so basically an error can let you stay logged in.
      });
  }, [decodedToken]);

  useEffect(() => {
    if (!decodedToken) {
      return;
    }

    if (isExpired(decodedToken)) {
      refreshToken();
      return;
    }

    const timeout = getTokenExpirationTimeout(decodedToken);
    const timeoutId = setTimeout(refreshToken, timeout);

    return () => clearTimeout(timeoutId);
  }, [decodedToken, refreshToken]);

  if (!children) {
    return null;
  }

  return isAuthenticated ? children : <AppLoader color={color} />;
};

AuthManager.propTypes = {
  color: PropTypes.string,
  forceAuthentication: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

AuthManager.defaultProps = {
  color: 'black',
  forceAuthentication: true,
};

export default AuthManager;
